import { useCallback, useEffect, useRef, useState } from 'react';
import type { Difficulty } from '../game/generator';
import {
  createSession,
  displayedElapsedMs,
  reduceSession,
  type GameSession,
  type SessionAction,
} from '../game/session';
import type { GeneratorClient } from '../platform/generation/generator-client';
import { createBrowserGenerator } from '../platform/generation/browser-generator';
import { loadSession, saveSession } from '../platform/session/session-storage';

const MAX_SEED = 2_147_483_647;
const TIMER_INTERVAL_MS = 1_000;

export type SudokuGameModel =
  | { phase: 'generating'; difficulty: Difficulty; session: null }
  | { phase: 'failed'; difficulty: Difficulty; session: null }
  | { phase: 'ready'; difficulty: Difficulty; session: GameSession };

export type SudokuGameController = {
  model: SudokuGameModel;
  nowMs: number;
  dispatch: (action: SessionAction) => void;
  newGame: (difficulty: Difficulty) => void;
};

function initialModel(): SudokuGameModel {
  const restored = loadSession(window.localStorage);
  return restored === null
    ? { phase: 'generating', difficulty: 'easy', session: null }
    : { phase: 'ready', difficulty: restored.difficulty, session: restored };
}

function nextSeed(): number {
  return Math.max(1, Math.floor(Date.now() % MAX_SEED));
}

export function useSudokuGame(): SudokuGameController {
  const [model, setModel] = useState<SudokuGameModel>(initialModel);
  const [nowMs, setNowMs] = useState(Date.now);
  const clientRef = useRef<GeneratorClient | null>(null);
  const modelRef = useRef(model);

  useEffect(() => {
    modelRef.current = model;
  }, [model]);

  const generate = useCallback((difficulty: Difficulty): void => {
    setModel({ phase: 'generating', difficulty, session: null });
    clientRef.current?.generate(difficulty, nextSeed(), (response) => {
      if (response.type === 'failed') {
        setModel({ phase: 'failed', difficulty, session: null });
      } else {
        setModel({
          phase: 'ready',
          difficulty: response.difficulty,
          session: createSession(response.puzzle, response.difficulty, Date.now()),
        });
      }
    });
  }, []);

  useEffect(() => {
    const client = createBrowserGenerator();
    clientRef.current = client;
    if (modelRef.current.session === null && modelRef.current.phase === 'generating') {
      generate(modelRef.current.difficulty);
    }
    return () => {
      clientRef.current = null;
      client.dispose();
    };
  }, [generate]);

  const dispatch = useCallback((action: SessionAction): void => {
    setModel((current) => {
      if (current.session === null) {
        return current;
      }
      const timedAction: SessionAction =
        action.type === 'enterValue' ? { ...action, nowMs: Date.now() } : action;
      return {
        ...current,
        session: reduceSession(current.session, timedAction),
      };
    });
  }, []);

  useEffect(() => {
    if (model.session !== null) {
      saveSession(window.localStorage, model.session, Date.now());
    }
  }, [model.session]);

  useEffect(() => {
    if (model.session?.status !== 'playing') {
      return;
    }
    const intervalId = window.setInterval(() => setNowMs(Date.now()), TIMER_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [model.session?.status]);

  useEffect(() => {
    const handleVisibility = (): void => {
      const current = modelRef.current;
      if (current.session === null) {
        return;
      }
      if (document.hidden) {
        dispatch({ type: 'pause', nowMs: Date.now(), automatic: true });
      } else {
        dispatch({ type: 'resumeAutomatic', nowMs: Date.now() });
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [dispatch]);

  useEffect(() => {
    const persistBeforeExit = (): void => {
      const current = modelRef.current.session;
      if (current !== null) {
        saveSession(window.localStorage, current, Date.now());
      }
    };
    window.addEventListener('pagehide', persistBeforeExit);
    return () => window.removeEventListener('pagehide', persistBeforeExit);
  }, []);

  return {
    model,
    nowMs: model.session === null ? nowMs : displayedElapsedMs(model.session, nowMs),
    dispatch,
    newGame: generate,
  };
}
